import { useQuery } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import React, { Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Card,
  EluveCardContent,
  EluveCardHeader,
  LoadingIndicator,
  P,
  PageTitle,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { UserGroupCodeConfigs } from './UserGroupCodeConfigs';
import { UserGroupUsers } from './UserGroupUsers';

const userGroupNameFragment = graphql(`
  fragment GroupName on UserGroups @_unmask {
    __typename
    id
    name
  }
`);

const getUserGroupNameQuery = graphql(
  `
    query getUserGroupName($tenantId: uuid!, $id: uuid!) {
      userGroupsByPk(tenantId: $tenantId, id: $id) {
        ...GroupName
      }
    }
  `,
  [userGroupNameFragment],
);

const useUserGroupName = (tenantId: string, groupId: string) => {
  const group = useCompleteFragment({
    fragment: userGroupNameFragment,
    key: {
      id: groupId,
    },
    strict: false,
  });

  useQuery(getUserGroupNameQuery, {
    fetchPolicy: 'cache-first',
    variables: {
      tenantId,
      id: groupId,
    },
    skip: Boolean(group),
  });

  return group?.name;
};

export const UserGroupDetailsPage: React.FC<{}> = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };

  const groupName = useUserGroupName(tenantId, userGroupId);

  return (
    <div className="container pb-11">
      <div className="grid gap-5">
        <PageTitle>{groupName}</PageTitle>
      </div>

      <ErrorBoundary fallback={<div>There was an error</div>}>
        <Suspense fallback={<LoadingIndicator />}>
          <div className="grid gap-5">
            <Card className="w-full">
              <EluveCardHeader>
                <P>Users</P>
              </EluveCardHeader>
              <EluveCardContent>
                <UserGroupUsers />
              </EluveCardContent>
            </Card>

            <UserGroupCodeConfigs />
          </div>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};
